import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, tap, concatMap } from 'rxjs';
import { switchMap, map, catchError, withLatestFrom, filter } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AppState } from "../../../shared/state/app.state";
import { DrawingService } from "../../data-access/drawing.service";
import {
  downloadDrawing,
  downloadDrawingFailure,
  downloadDrawingSuccess, loadCommonDrawingBlob, loadCommonDrawingBlobFailure, loadCommonDrawingBlobSuccess,
  loadCommonDrawings,
  loadCommonDrawingsFailure,
  loadCommonDrawingsSuccess, modifyDrawing, modifyDrawingFailure, modifyDrawingSuccess,
  removeBulkDrawing,
  removeBulkDrawingFailure,
  removeBulkDrawingSuccess, saveCommonDrawingScribble, saveCommonDrawingScribbleFailure, saveCommonDrawingScribbleSuccess, uploadCommonDrawings, uploadCommonDrawingsFailure,
  uploadCommonDrawingsSuccess
} from "./drawing.action";
import { selectAllDrawings, selectCommonDrawingBlobMap, selectDrawings } from "./drawing.selectors";
import { toggleDrawingRedline } from "../../../cad-model-upload/state/cad-model-revision-drawing/cad-model-revision-drawing.action";
import { ToastMessageOptions, MessageService } from "primeng/api";
import { GetMessage } from "../../../global-config/utils/toast-messages.utils";


@Injectable()
export class DrawingEffects {
  loadDrawings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCommonDrawings, uploadCommonDrawingsSuccess),
      switchMap(() =>
        this.drawingService.get()
          .pipe(
            map((drawings) => loadCommonDrawingsSuccess({ drawings: drawings })),
            catchError((error) => of(loadCommonDrawingsFailure({ error })))
          )
      )
    )
  );

  downloadDrawing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(downloadDrawing),
      switchMap((action) => {
        return this.drawingService.download(action.drawingId, action.fileName)
          .pipe(
            map((file) => downloadDrawingSuccess({ file: file })),
            catchError((error) => of(downloadDrawingFailure({ error: GetMessage('DownloadFailure', 'Drawing') })))
          )
      }
      )
    )
  );

  modifyDrawing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(modifyDrawing),
      concatMap((action) => {
        return this.drawingService.modifyDrawing(action.drawing)
          .pipe(
            map((drawing) => modifyDrawingSuccess({
              drawing: drawing, successMessage: GetMessage('ModifySuccess', 'Drawing')
            })),
            catchError((error) => of(modifyDrawingFailure({ error: GetMessage('ModifyFailure', 'Drawing') })))
          )
      }
      )
    )
  );

  removeBulkDrawing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeBulkDrawing),
      withLatestFrom(this.store.select(selectDrawings)),
      switchMap(([action]) => {
        return this.drawingService.removeBulk(action.drawingIds)
          .pipe(
            map((response) => removeBulkDrawingSuccess({ drawingIds: response?.data?.drawingIds ?? [], successMessage: GetMessage('RemoveSuccess', 'Drawing') })),
            catchError((error) => of(removeBulkDrawingFailure({ error: GetMessage('RemoveFailure', 'Drawing') })))
          )
      }
      )
    )
  );

  uploadCommonDrawings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(uploadCommonDrawings),
      switchMap((action) => {
        return this.drawingService.uploadCommonDrawings(action.files)
          .pipe(
            map((drawing) => uploadCommonDrawingsSuccess({ drawing: drawing.data, successMessage: GetMessage('UploadSuccess', 'Drawing') })),
            catchError((error) => of(uploadCommonDrawingsFailure({ error: GetMessage('UploadFailure', 'Drawing') })))
          )
      }
      )
    )
  );

  loadCommonDrawingBlob = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCommonDrawingBlob),
      withLatestFrom(this.store.select(selectCommonDrawingBlobMap)),
      filter(([action, blobDrawingMap]) => {
        if (!(action.pdfDrawingFileKey in blobDrawingMap)) {
          return true;
        }
        return blobDrawingMap[action.pdfDrawingFileKey].inProgress === true && blobDrawingMap[action.pdfDrawingFileKey].previousCallInProgress === false && blobDrawingMap[action.pdfDrawingFileKey].completed === false;
      }),
      concatMap(([action, blobDrawingMap]) =>
        this.drawingService.downloadByPdfDrawingKey(action.pdfDrawingFileKey)
          .pipe(
            map((blob) => loadCommonDrawingBlobSuccess({ pdfDrawingFileKey: action.pdfDrawingFileKey, blob })),
            catchError((error) => of(loadCommonDrawingBlobFailure({ error })))
          )
      )
    )
  );

  saveCommonDrawingScribble$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveCommonDrawingScribble),
      switchMap(({ drawingId, pdfDrawingFileKey, blob }) =>
        this.drawingService.saveScribble(drawingId, blob).pipe(
          map(() => saveCommonDrawingScribbleSuccess({ drawingId, pdfDrawingFileKey, blob })),
          catchError(() => of(saveCommonDrawingScribbleFailure({ error: 'Failed to save Markup' })))
        )
      )
    )
  );

  enableRedlineOnCommonScribbleSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveCommonDrawingScribbleSuccess),
      withLatestFrom(this.store.select(selectAllDrawings)),
      filter(([{ drawingId }, drawings]) => {
        const d = drawings.find((d: any) => d.id === drawingId);
        return d ? !(d.isRedline ?? false) : false;
      }),
      map(([{ drawingId }]) => toggleDrawingRedline({ drawingId }))
    )
  );


  toastSuccessMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        modifyDrawingSuccess,
        removeBulkDrawingSuccess,
        uploadCommonDrawingsSuccess
      ),
      tap(({ successMessage }) => {
        return this.messageService.add({
          severity: 'success',
          summary: successMessage,
        } as ToastMessageOptions)
      }
      )
    ),
    { dispatch: false }
  );

  toastErrorMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        loadCommonDrawingsFailure,
        uploadCommonDrawingsFailure,
        modifyDrawingFailure,
        removeBulkDrawingFailure,
        downloadDrawingFailure,
        saveCommonDrawingScribbleFailure
      ),
      tap(({ error }) => {
        return this.messageService.add({
          severity: 'error',
          summary: error,
        } as ToastMessageOptions)
      }
      )
    ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private drawingService: DrawingService,
    private messageService: MessageService,
  ) { }
}
